import PortalIdParser from 'PortalIdParser';
import { getPropertyValue } from 'marketing-platform-lib/utils/crmUtils';
import { OBJECT_TYPE } from 'marketing-platform-lib/constants/crm';
const AD_NETWORK_TYPE = {
  FACEBOOK: 'FACEBOOK',
  GOOGLE: 'GOOGLE',
  LINKEDIN: 'LINKEDIN'
};
function getNetworkNameForApiCall(crmObject) {
  const hsAdNetwork = getPropertyValue(crmObject, 'hs_ad_network');
  switch (hsAdNetwork) {
    default:
    case AD_NETWORK_TYPE.FACEBOOK:
      return 'facebook';
    case AD_NETWORK_TYPE.GOOGLE:
      return 'adwords';
    case AD_NETWORK_TYPE.LINKEDIN:
      return 'linkedin';
  }
}

/**
 * Returns the href for the details/record page for @objectTypeId and @objectId.
 * This method **should not** be exported because it's just being used as a fallback to @getObjectHref (method below).
 * Otherwise we'll be generating wrong urls for Marketing assets.
 */
function _getRecordHref(objectTypeId, objectId) {
  const portalId = PortalIdParser.get();
  switch (objectTypeId) {
    default:
      return '';
    case OBJECT_TYPE.CONTACT:
      return `/contacts/${portalId}/contact/${objectId}`;
    case OBJECT_TYPE.COMPANY:
      return `/contacts/${portalId}/company/${objectId}`;
    case OBJECT_TYPE.DEAL:
      return `/contacts/${portalId}/deal/${objectId}`;
    case OBJECT_TYPE.TICKET:
      return `/contacts/${portalId}/ticket/${objectId}`;
    case OBJECT_TYPE.BLOG_POST:
      return `/content-detail/${portalId}/blog-post/${objectId}/performance`;
    case OBJECT_TYPE.LANDING_PAGE:
      return `/content-detail/${portalId}/landing-page/${objectId}/performance`;
    case OBJECT_TYPE.FORM:
      return `/forms/${portalId}/${objectId}/performance`;
    case OBJECT_TYPE.MARKETING_EMAIL:
      return `/email/${portalId}/details/${objectId}/performance`;
    case OBJECT_TYPE.CAMPAIGN:
      return `/marketing/${portalId}/campaigns/${objectId}/performance`;
    case OBJECT_TYPE.SITE_PAGE:
      return `/content-detail/${portalId}/site-page/${objectId}/performance`;
    case OBJECT_TYPE.SOCIAL_BROADCAST:
      return `/social/${portalId}/manage?broadcast=${objectId}`;
    case OBJECT_TYPE.CTA:
      return `/ctas/${portalId}/details/${objectId}`;
    case OBJECT_TYPE.WORKFLOW:
      return `/workflows/${portalId}/details/${objectId}/performance`;
    case OBJECT_TYPE.MARKETING_EVENT:
      return `/marketing-events/${portalId}/details/${objectId}`;
    case OBJECT_TYPE.WEB_INTERACTIVES:
      return `/calls-to-action/${portalId}/edit/${objectId}/content`;
    case OBJECT_TYPE.AUTOMATION_JOURNEY:
      return `/journeys/${portalId}/details/${objectId}`;
  }
}

/**
 * Returns the href for the details/record page of @crmObject.
 *
 * Most marketing assets (Campaign, Marketing email, forms, Workflow, CTA...) don't share a record page just like
 * CONTACT, COMPANY, TICKET and DEAL.
 *
 * This method extracts the property value from @crmObject (usually called hs_origin_asset_id, but it could be other property name as defined by the team that owns the object type) and returns the correct href link.
 */
export function getObjectHref(crmObject) {
  const portalId = PortalIdParser.get();
  switch (crmObject.objectTypeId) {
    default:
      return '';
    case OBJECT_TYPE.CONTACT:
    case OBJECT_TYPE.COMPANY:
    case OBJECT_TYPE.DEAL:
    case OBJECT_TYPE.TICKET:
    case OBJECT_TYPE.AUTOMATION_JOURNEY:
      return _getRecordHref(crmObject.objectTypeId, crmObject.objectId);
    case OBJECT_TYPE.CAMPAIGN:
    case OBJECT_TYPE.SITE_PAGE:
    case OBJECT_TYPE.MARKETING_EMAIL:
    case OBJECT_TYPE.LANDING_PAGE:
    case OBJECT_TYPE.BLOG_POST:
    case OBJECT_TYPE.SOCIAL_BROADCAST:
    case OBJECT_TYPE.CTA:
      return _getRecordHref(crmObject.objectTypeId, `${getPropertyValue(crmObject, 'hs_origin_asset_id')}`);
    case OBJECT_TYPE.FORM:
      return _getRecordHref(crmObject.objectTypeId, `${getPropertyValue(crmObject, 'hs_form_id')}`);
    case OBJECT_TYPE.AD_CAMPAIGN:
      {
        const network = getNetworkNameForApiCall(crmObject);
        const hsAdAccountId = getPropertyValue(crmObject, 'hs_ad_account_id');
        const hsAdCampaignId = getPropertyValue(crmObject, 'hs_ad_campaign_id');
        return `/ads/${portalId}/accounts/${network}/${hsAdAccountId}/campaigns/${hsAdCampaignId}`;
      }
    case OBJECT_TYPE.AD_GROUP:
      {
        const network = getNetworkNameForApiCall(crmObject);
        const hsAdAccountId = getPropertyValue(crmObject, 'hs_ad_account_id');
        const hsAdCampaignId = getPropertyValue(crmObject, 'hs_ad_campaign_id');
        const hsAdGroupId = getPropertyValue(crmObject, 'hs_ad_group_id');
        return `/ads/${portalId}/accounts/${network}/${hsAdAccountId}/campaigns/${hsAdCampaignId}/adgroups/${hsAdGroupId}`;
      }
    case OBJECT_TYPE.WORKFLOW:
      return _getRecordHref(crmObject.objectTypeId, `${getPropertyValue(crmObject, 'hs_flow_id')}`);
    case OBJECT_TYPE.OBJECT_LIST:
      {
        const hsObjectTypeId = getPropertyValue(crmObject, 'hs_object_type_id');
        if (hsObjectTypeId === OBJECT_TYPE.CONTACT) {
          const hsListId = getPropertyValue(crmObject, 'hs_classic_list_id');
          return `/contacts/${portalId}/lists/${hsListId}/performance`;
        } else if (hsObjectTypeId === OBJECT_TYPE.COMPANY) {
          const hsListId = getPropertyValue(crmObject, 'hs_list_id');
          return `/contacts/${portalId}/objectLists/${hsListId}/performance`;
        }
        return '';
      }
    case OBJECT_TYPE.MARKETING_EVENT:
      return _getRecordHref(crmObject.objectTypeId, crmObject.objectId);
    case OBJECT_TYPE.WEB_INTERACTIVES:
      return _getRecordHref(crmObject.objectTypeId, `${getPropertyValue(crmObject, 'hs_content_id')}`);
    case OBJECT_TYPE.AUTOMATION_PLATFORM_FLOW_ACTION:
      {
        const actionId = getPropertyValue(crmObject, 'hs_action_id');
        const flowId = getPropertyValue(crmObject, 'hs_flow_id');
        return `/workflows/${portalId}/platform/flow/${flowId}/edit/actions/${actionId}`;
      }
  }
}

/**
 * Returns the href for the listing page of @crmObject.
 */
export function getObjectListingHref(objectTypeId) {
  const portalId = PortalIdParser.get();
  switch (objectTypeId) {
    default:
      return '';
    case OBJECT_TYPE.CONTACT:
    case OBJECT_TYPE.COMPANY:
    case OBJECT_TYPE.DEAL:
    case OBJECT_TYPE.TICKET:
      return `/contacts/${portalId}/objects/${objectTypeId}`;
    case OBJECT_TYPE.MARKETING_EVENT:
      return `/contacts/${portalId}/objects/0-54`;
    case OBJECT_TYPE.BLOG_POST:
      return `/website/${portalId}/blog`;
    case OBJECT_TYPE.LANDING_PAGE:
      return `/website/${portalId}/pages/landing`;
    case OBJECT_TYPE.SITE_PAGE:
      return `/website/${portalId}/pages/site`;
    case OBJECT_TYPE.FORM:
      return `/forms/${portalId}`;
    case OBJECT_TYPE.MARKETING_EMAIL:
      return `/email/${portalId}/manage`;
    case OBJECT_TYPE.CAMPAIGN:
      return `/marketing/${portalId}/campaigns`;
    case OBJECT_TYPE.SOCIAL_BROADCAST:
      return `/social/${portalId}/manage`;
    case OBJECT_TYPE.CTA:
      return `/ctas/${portalId}`;
    case OBJECT_TYPE.WORKFLOW:
      return `/workflows/${portalId}`;
    case OBJECT_TYPE.MARKETING_SMS:
      return `/sms/${portalId}`;
    case OBJECT_TYPE.WEB_INTERACTIVES:
      return `/calls-to-action/${portalId}/manage/state`;
    case OBJECT_TYPE.AUTOMATION_JOURNEY:
      return `/journeys/${portalId}`;
  }
}

/**
 * Returns the href for the editor page of @crmObject.
 */
export function getObjectEditorHref(crmObject) {
  const portalId = PortalIdParser.get();
  switch (crmObject.objectTypeId) {
    default:
      return '';
    case OBJECT_TYPE.BLOG_POST:
      return `/blog/${portalId}/editor/${getPropertyValue(crmObject, 'hs_origin_asset_id')}/content`;
    case OBJECT_TYPE.LANDING_PAGE:
    case OBJECT_TYPE.SITE_PAGE:
      return `/pages/${portalId}/editor/${getPropertyValue(crmObject, 'hs_origin_asset_id')}/content`;
    case OBJECT_TYPE.MARKETING_EMAIL:
      return `/email/${portalId}/edit/${getPropertyValue(crmObject, 'hs_origin_asset_id')}/content`;
    case OBJECT_TYPE.AUTOMATION_JOURNEY:
      return `/journeys/${portalId}/journey/${crmObject.objectId}`;
    case OBJECT_TYPE.WEB_INTERACTIVES:
      return `/calls-to-action/${portalId}/edit/${getPropertyValue(crmObject, 'hs_content_id')}/content`;
    case OBJECT_TYPE.SOCIAL_BROADCAST:
      return `/social/${portalId}/compose?broadcastGuids=${getPropertyValue(crmObject, 'hs_origin_asset_id')}`;
    case OBJECT_TYPE.FORM:
      return `/lead-flows/${portalId}/edit/${getPropertyValue(crmObject, 'hs_origin_asset_id')}`;
    case OBJECT_TYPE.MARKETING_SMS:
      return `/sms/${portalId}/edit/${getPropertyValue(crmObject, 'hs_content_id')}/content`;
  }
}
export function getObjectRevisionsHref(crmObject) {
  const portalId = PortalIdParser.get();
  switch (crmObject.objectTypeId) {
    default:
      return '';
    case OBJECT_TYPE.BLOG_POST:
      return `/blog/${portalId}/editor/${getPropertyValue(crmObject, 'hs_origin_asset_id')}/revisions`;
    case OBJECT_TYPE.LANDING_PAGE:
    case OBJECT_TYPE.SITE_PAGE:
      return `/pages/${portalId}/editor/${getPropertyValue(crmObject, 'hs_origin_asset_id')}/revisions`;
    case OBJECT_TYPE.MARKETING_EMAIL:
      return `/email/${portalId}/edit/${getPropertyValue(crmObject, 'hs_origin_asset_id')}/revisions`;
  }
}

/**
 * Creates the URL for the details page in https://git.hubteam.com/HubSpot/switchboard-ui
 * @param objectTypeId
 * @param objectId
 */
export function getGoToDetailsHref(objectTypeId, objectId, search) {
  const portalId = PortalIdParser.get();
  return `/go-to/${portalId}/${objectTypeId}/${objectId}${search || ''}`;
}

/**
 * Creates the URL for the listing page in https://git.hubteam.com/HubSpot/switchboard-ui
 * @param objectTypeId
 */
export function getGoToListingHref(objectTypeId, search) {
  const portalId = PortalIdParser.get();
  return `/go-to/${portalId}/${objectTypeId}${search || ''}`;
}

/**
 * Creates the URL for the editor page in https://git.hubteam.com/HubSpot/switchboard-ui
 */
export function getGoToEditorHref({
  objectId,
  objectTypeId,
  search
}) {
  const portalId = PortalIdParser.get();
  return `/go-to/${portalId}/${objectTypeId}/${objectId}/edit${search || ''}`;
}

/**
 * Creates the URL for the revisions page in https://git.hubteam.com/HubSpot/switchboard-ui
 */
export function getGoToRevisionsHref({
  objectId,
  objectTypeId,
  search
}) {
  const portalId = PortalIdParser.get();
  return `/go-to/${portalId}/${objectTypeId}/${objectId}/revisions${search || ''}`;
}